<template>
  <div class="modal-body customer-step">
    <div class="registration-errors" v-show="errorMessage">
      <div class="alert alert-danger">{{ errorMessage }}</div>
    </div>

    <div><h5>Customer Info</h5></div>

    <validation-observer v-slot="{ invalid, valid, errors, handleSubmit, ...rest }" ref="customerForm">
      <div class="customer-info-form">
        <form @submit.prevent="handleSubmit(submit)">
          <!-- first_name -->
          <validation-provider vid="first_name" rules="required" v-slot="{errors}">
            <div class="form-group">
              <label for="first_name" class="control-label text-input">First Name <span class="asteric">*</span></label>
              <input
                id="first_name"
                type="text"
                class="form-control"
                :class="{'is-invalid': errors && errors[0]}"
                name="first_name"
                v-model="model.first_name"
                maxlength="50"
                required
              />
              <span v-if="errors && errors[0]" class="input-error text-danger">{{errors[0]}}</span>
            </div>
          </validation-provider>

          <!-- last_name -->
          <validation-provider vid="last_name" rules="required" v-slot="{errors}">
            <div class="form-group">
              <label for="last_name" class="control-label text-input">Last Name <span class="asteric">*</span></label>
              <input
                id="last_name"
                type="text"
                class="form-control"
                :class="{'is-invalid': errors && errors[0]}"
                name="last_name"
                v-model="model.last_name"
                maxlength="50"
              />
              <span v-if="errors && errors[0]" class="input-error text-danger">{{errors[0]}}</span>
            </div>
          </validation-provider>

          <!-- email -->
          <validation-provider vid="email" rules="required" v-slot="{errors}">
            <div class="form-group">
              <label for="email" class="control-label text-input">Email <span class="asteric">*</span></label>
              <input
                id="email"
                type="email"
                class="form-control"
                :class="{'is-invalid': errors && errors[0]}"
                name="email"
                v-model="model.email"
                maxlength="250"
              />
              <span v-if="errors && errors[0]" class="input-error text-danger">{{errors[0]}}</span>
            </div>
          </validation-provider>

          <!-- email_confirmation -->
          <validation-provider vid="email_confirmation" rules="required" v-slot="{errors}">
            <div class="form-group">
              <label for="email_confirmation" class="control-label text-input">Email Confirmation <span class="asteric">*</span></label>
              <input
                id="email_confirmation"
                type="email"
                class="form-control"
                :class="{'is-invalid': errors && errors[0]}"
                name="email_confirmation"
                v-model="model.email_confirmation"
                maxlength="250"
              />
              <span v-if="errors && errors[0]" class="input-error text-danger">{{errors[0]}}</span>
            </div>
          </validation-provider>

          <!-- company -->
          <validation-provider vid="company" v-slot="{errors}">
            <div class="form-group">
              <label for="company" class="control-label text-input">Company</label>
              <input
                id="company"
                type="text"
                class="form-control"
                :class="{'is-invalid': errors && errors[0]}"
                name="company"
                v-model="model.company"
                maxlength="80"
              />
              <span v-if="errors && errors[0]" class="input-error text-danger">{{errors[0]}}</span>
            </div>
          </validation-provider>

          <!-- country_id -->
          <validation-provider vid="country_id" rules="required" v-slot="{errors}">
            <div class="form-group">
              <label for="country_id" class="control-label text-input">Country <span class="asteric">*</span></label>
              <select
                id="country_id"
                class="form-control"
                :class="{'is-invalid': errors && errors[0]}"
                v-model="model.country_id"
              >
                <option v-for="(option, index) in countries" :key="`country-option-${index}`" :value="option.id">
                  {{ option.name }}
                </option>
              </select>
              <span v-if="errors && errors[0]" class="input-error text-danger">{{errors[0]}}</span>
            </div>
          </validation-provider>

          <!-- address -->
          <validation-provider vid="address" rules="required" v-slot="{errors}">
            <div class="form-group">
              <label for="address" class="control-label text-input">Billing Address <span class="asteric">*</span></label>
              <input
                id="address"
                type="text"
                class="form-control"
                :class="{'is-invalid': errors && errors[0]}"
                name="address"
                v-model="model.address"
              />
              <span v-if="errors && errors[0]" class="input-error text-danger">{{errors[0]}}</span>
            </div>
          </validation-provider>

          <!-- city -->
          <validation-provider vid="city" rules="required" v-slot="{errors}">
            <div class="form-group">
              <label for="city" class="control-label text-input">City <span class="asteric">*</span></label>
              <input
                id="city"
                type="text"
                class="form-control"
                :class="{'is-invalid': errors && errors[0]}"
                name="city"
                v-model="model.city"
              />
              <span v-if="errors && errors[0]" class="input-error text-danger">{{errors[0]}}</span>
            </div>
          </validation-provider>

          <!-- state_id (US) -->
          <validation-provider v-show="isUnitedStates" vid="state_id" :rules="isUnitedStates ? 'required' : ''" v-slot="{errors}">
            <div class="form-group">
              <label for="state_id" class="control-label text-input">State <span class="asteric">*</span></label>
              <select
                id="state_id"
                class="form-control"
                :class="{'is-invalid': errors && errors[0]}"
                v-model="model.state_id"
              >
                <option v-for="(option, index) in states" :key="`state-option-${index}`" :value="option.id">
                  {{ option.name }}
                </option>
              </select>
              <span v-if="errors && errors[0]" class="input-error text-danger">{{errors[0]}}</span>
            </div>
          </validation-provider>

          <!-- state_region (non-US) -->
          <validation-provider v-show="!isUnitedStates" vid="other_state" :rules="!isUnitedStates ? 'required' : ''" v-slot="{errors}">
            <div class="form-group">
              <label for="other_state" class="control-label text-input">State / Region <span class="asteric">*</span></label>
              <input
                id="other_state"
                type="text"
                class="form-control"
                :class="{'is-invalid': errors && errors[0]}"
                name="other_state"
                v-model="model.other_state"
              />
              <span v-if="errors && errors[0]" class="input-error text-danger">{{errors[0]}}</span>
            </div>
          </validation-provider>

          <!-- zip -->
          <validation-provider vid="zip" rules="required" v-slot="{errors}">
            <div class="form-group">
              <label for="zip" class="control-label text-input">{{ isUnitedStates ? 'Zip' : 'Postal Code' }} <span class="asteric">*</span></label>
              <input
                id="zip"
                type="text"
                class="form-control"
                :class="{'is-invalid': errors && errors[0]}"
                name="zip"
                v-model="model.zip"
              />
              <span v-if="errors && errors[0]" class="input-error text-danger">{{errors[0]}}</span>
            </div>
          </validation-provider>

          <!-- phone -->
          <validation-provider vid="phone" rules="required" v-slot="{errors}">
            <div class="form-group">
              <label for="phone" class="control-label text-input">Phone Number <span class="asteric">*</span></label>
              <input
                id="phone"
                type="text"
                class="form-control"
                :class="{'is-invalid': errors && errors[0]}"
                name="phone"
                v-model="model.phone"
              />
              <span v-if="errors && errors[0]" class="input-error text-danger">{{errors[0]}}</span>
            </div>
          </validation-provider>
        </form>
      </div>
    </validation-observer>
  </div>
</template>

<script>
import {mapActions, mapGetters} from "vuex";
import {scrollToNotValidField} from "../../helpers/scrollToNotValidField";

export default {
  props: {
    isAttendee: {
      type: Boolean,
      default: false
    }
  },
  mounted() {
    if (this.$refs.customerForm) {
      this.$refs.customerForm.reset();
    }
  },
  computed: {
    ...mapGetters("basketStore", [
      "conference",
      "slug",
      "tickets",
      "customer",
      "states",
      "countries",
      "errorMessage"
    ]),
    isUnitedStates() {
      return this.model.country_id === 232;
    }
  },
  data() {
    return {
      model: {
        first_name: '',
        last_name: '',
        email: '',
        email_confirmation: '',
        company: '',
        country_id: 232,
        address: '',
        city: '',
        state_id: '',
        other_state: '',
        zip: '',
        phone: ''
      }
    }
  },
  watch: {
    customer: {
      handler(newVal) {
        this.model = { ...newVal };
      },
      deep: true,
      immediate: true
    },
    'model.country_id'(newVal, oldVal) {
      if (oldVal && newVal !== oldVal) {
        this.model.state_id = '';
        this.model.other_state = '';
      }
    }
  },
  methods: {
    ...mapActions('basketStore', ['updateCustomer']),

    submitForm() {
      this.$refs.customerForm.handleSubmit(this.submit);
    },

    submit: async function () {
      const data = {
        customer: {
          ...this.model,
          is_attendee: this.isAttendee
        },
        success: () => {
          // go to next step
          this.$store.dispatch('basketStore/changeStep', { type: "forward" });
          this.$nextTick(() => {
            // this.$refs.customerForm.reset();
          });
          this.code = null;
        },
        error: (err) => {
          if (err.errors) {
            this.$refs.customerForm.setErrors(err.errors);
            scrollToNotValidField(err.errors, '.modal-body.customer-step')
          }
        }
      };

      this.$refs.customerForm.validate().then(success => {
        if (!success) {
          return
        }

        this.updateCustomer(data);
      });
    }
  }
}
</script>

<style lang="scss" scoped>
.form-group .control-label {
  font-weight: 400;
}
</style>
